import React, { useMemo, useState } from "react";

import { useQuery, useSubscription } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Grid } from "@mui/material";
import { isEmpty, sortBy } from "lodash";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { MealKind } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import { formatISODate, formatISOTimeOfDay } from "@/utils/datetime";

import AddNewMealType from "./components/AddNewMealTypeModal";
import MealComments from "./components/MealComments";
import MealDetails from "./components/MealDetails";
import MealTab from "./components/MealTab";
import TotalCaloriesConsumed from "./components/TotalCaloriesConsumed";
import { useCreateMeal } from "./utils/useCreateMeal";

type Params = {
  clientId: string;
  date: string;
};

interface MealReference {
  mealType: MealKind;
  mealId?: string | undefined;
  time: string; // for sorting and creating meal for no meal comments only
}

const STANDARD_CALORIE_DEFICIT = 500;

export const clientDiaryMealPageQuery = graphql(`
  query ClientDiaryMealPage(
    $clientId: String
    $clientId2: ID!
    $consumedOn: ISO8601Date!
  ) {
    meals(clientId: $clientId, consumedOn: $consumedOn) {
      nodes {
        ...MealTab
        ...MealDetails
        ...MealComments
        consumedAt
        carbohydrateGrams
        proteinGrams
        fatGrams
      }
    }

    client(id: $clientId2) {
      id
      dailyEnergyExpenditure
      programForDate(date: $consumedOn) {
        id
        mealPlan {
          id
          recommendedCalories
          plannedMeals {
            id
            meal
            recommendedTime
          }
        }
      }
    }

    coachViewer {
      id
    }
  }
`);

export const mealCreatedSubscription = graphql(`
  subscription MealCreated {
    synchronize(model: "Meal", action: CREATE) {
      node {
        ... on Meal {
          id
          consumedOn
        }
      }
    }
  }
`);

const ClientDiaryMealPage: React.FC = () => {
  const { clientId, date } = useParams<Params>();
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const addNewMealTypeDialog = useDialogState();

  const { data, refetch } = useQuery(clientDiaryMealPageQuery, {
    variables: {
      clientId: clientId,
      clientId2: clientId ?? "",
      consumedOn: date ?? formatISODate(new Date()),
    },
  });
  useSubscription(mealCreatedSubscription, {
    onData: ({ data }) => {
      const createdMeal = data.data?.synchronize.node;
      if (!createdMeal || createdMeal.__typename !== "Meal") return;
      if (createdMeal.consumedOn !== date) return;
      refetch();
    },
  });

  const createMeal = useCreateMeal();
  const addMealType = async (mealType: MealKind) => {
    await createMeal({
      mealType: mealType.toLowerCase(),
      clientId: clientId,
      consumedOn: date,
      consumedAt: formatISOTimeOfDay(new Date()),
    });
  };

  const plannedMeals =
    data?.client?.programForDate?.mealPlan?.plannedMeals ?? [];
  const existingMeals = data?.meals.nodes ?? [];

  const meals: MealReference[] = useMemo(() => {
    const _meals: MealReference[] = [];

    if (plannedMeals.length === 0) {
      // Default breakfast, lunch, dinner, snack
      _meals.push(
        { mealType: MealKind.Breakfast, time: "07:00:00" },
        { mealType: MealKind.Lunch, time: "12:00:00" },
        { mealType: MealKind.Dinner, time: "19:00:00" },
        { mealType: MealKind.Snack, time: "20:00:00" }
      );
    } else {
      plannedMeals.forEach((pm) => {
        _meals.push({ mealType: pm.meal, time: pm.recommendedTime });
      });
    }

    existingMeals.forEach((meal) => {
      const existingPlannedIndex = _meals.findIndex(
        (m) => m.mealType === meal.mealType
      );
      if (existingPlannedIndex > -1) {
        _meals[existingPlannedIndex].mealId = meal.id;
        _meals[existingPlannedIndex].time = meal.consumedAt;
      } else {
        _meals.push({
          mealId: meal.id,
          mealType: meal.mealType,
          time: meal.consumedAt,
        });
      }
    });

    return sortBy(_meals, "time");
  }, [data]);

  const options = useMemo(() => {
    return Object.values(MealKind).filter(
      (kind) => !meals.find((m) => m.mealType === kind)
    );
  }, [meals]);

  const caloriesConsumed = useMemo(() => {
    return data?.meals.nodes.reduce(
      (previous, current) => previous + current.energyKcal,
      0
    );
  }, [data?.meals.nodes]);

  const macrosConsumed = useMemo(() => {
    const nodes = data?.meals.nodes ?? [];
    return {
      carbs: nodes.reduce((sum, meal) => sum + meal.carbohydrateGrams, 0),
      protein: nodes.reduce((sum, meal) => sum + meal.proteinGrams, 0),
      fat: nodes.reduce((sum, meal) => sum + meal.fatGrams, 0),
    };
  }, [data?.meals.nodes]);

  const targetCalorieGoal = useMemo(() => {
    const recommendedCalories =
      data?.client?.programForDate?.mealPlan?.recommendedCalories;
    const targetEnergyFromTDEE =
      (data?.client?.dailyEnergyExpenditure || STANDARD_CALORIE_DEFICIT) -
      STANDARD_CALORIE_DEFICIT;

    return recommendedCalories ?? targetEnergyFromTDEE;
  }, [data]);

  const scrollIntoView = (mealType: MealKind) => {
    document.getElementById(mealType)?.scrollIntoView();
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <div className={classes.tabs}>
            <TotalCaloriesConsumed
              caloriesConsumed={caloriesConsumed}
              recommendedCalories={targetCalorieGoal}
              macrosConsumed={macrosConsumed}
            />
            {meals.map((meal, index) => (
              <MealTab
                key={meal.mealType}
                mealType={meal.mealType}
                mealId={meal.mealId}
                active={index === activeTab}
                onClick={() => {
                  setActiveTab(index);
                  scrollIntoView(meal.mealType);
                }}
              />
            ))}
            <Box flex={1} textAlign="center">
              <Button
                disabled={isEmpty(options)}
                onClick={addNewMealTypeDialog.show}
                variant="text"
                startIcon={<AddIcon fontSize="small" />}
              >
                Add new meal type
              </Button>
            </Box>
          </div>
        </Grid>
        <Grid container item xs={12} md={9} spacing={4}>
          {meals.map((meal) => (
            <React.Fragment key={meal.mealId ?? meal.mealType}>
              <Grid item xs={12} md={7}>
                <MealDetails mealType={meal.mealType} id={meal.mealId} />
              </Grid>
              <Grid item xs={12} md={5}>
                <MealComments
                  mealId={meal.mealId}
                  coachId={data?.coachViewer.id}
                  createMealInput={{
                    mealType: meal.mealType,
                    clientId: clientId ?? "",
                    consumedOn: date ?? "",
                    consumedAt: meal.time,
                  }}
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Grid>
      <AddNewMealType
        open={addNewMealTypeDialog.shown}
        onClose={addNewMealTypeDialog.hide}
        onSubmit={(values) => addMealType(values.mealType)}
        mealOptions={options}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginTop: 24,
    padding: theme.spacing(0, 4),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0, 2),
    },
  },
  tabs: {
    position: "sticky",
    top: 0,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: "calc(100vh - 80px)",
    overflowX: "hidden",
    overflowY: "auto",
    [theme.breakpoints.down("md")]: {
      position: "static",
      maxHeight: "none",
    },
  },
}));

export default ClientDiaryMealPage;
